import { useEffect, useState } from 'react'
import { masterFingerprint, type FingerprintGlyph } from '../../crypto/fingerprint'

/**
 * Recalcula a impressão digital enquanto o usuário digita.
 *
 * O HMAC custa microssegundos, mas ainda assim há um pequeno atraso: sem ele,
 * as formas piscariam a cada tecla e perderiam a função de "marca reconhecível".
 */
export function Fingerprint({ master }: { master: string }) {
  const [glyphs, setGlyphs] = useState<FingerprintGlyph[] | null>(null)

  useEffect(() => {
    if (master.length === 0) {
      setGlyphs(null)
      return
    }
    let cancelled = false
    const timer = setTimeout(() => {
      masterFingerprint(master)
        .then((next) => {
          if (!cancelled) setGlyphs(next)
        })
        .catch(() => {
          if (!cancelled) setGlyphs(null)
        })
    }, 180)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [master])

  // Com a mestra vazia não há marca a mostrar, e três círculos tracejados só
  // roubariam largura do campo numa tela de 360 px.
  if (!glyphs) return null

  return (
    <div
      className="flex h-9 items-center gap-1 rounded-lg bg-surface px-1.5"
      // Decorativa para leitores de tela: descrevê-la em voz alta anunciaria
      // justamente o que essa marca não deve ser dito em voz alta.
      aria-hidden="true"
    >
      {glyphs.map((glyph, index) => (
        <FingerprintShapeBox key={index} glyph={glyph} />
      ))}
    </div>
  )
}

function FingerprintShapeBox({ glyph }: { glyph: FingerprintGlyph }) {
  const filled = glyph.shape >= 8
  const base = glyph.shape % 8
  const geometry = [
    <circle key="c" cx="12" cy="12" r="7.6" />,
    <rect key="s" x="4.6" y="4.6" width="14.8" height="14.8" rx="3" />,
    <path key="t" d="M12 4 20.5 19H3.5Z" />,
    <path key="d" d="M12 3.6 20.4 12 12 20.4 3.6 12Z" />,
    <path key="h" d="M12 3.6 19.3 7.8v8.4L12 20.4 4.7 16.2V7.8Z" />,
    <path
      key="star"
      d="M12 3.5 14.29 8.85 20.08 9.37 15.71 13.21 17 18.88 12 15.9 7 18.88 8.29 13.21 3.92 9.37 9.71 8.85Z"
    />,
    <path key="p" d="M9.5 4h5v5.5H20v5h-5.5V20h-5v-5.5H4v-5h5.5Z" />,
    <path
      key="drop"
      d="M12 3.5c4.5 5 6.5 7.7 6.5 10.5a6.5 6.5 0 0 1-13 0C5.5 11.2 7.5 8.5 12 3.5Z"
    />,
  ][base]

  return (
    <svg
      viewBox="0 0 24 24"
      className="size-5 animate-rise"
      strokeWidth={2.2}
      strokeLinejoin="round"
      fill={filled ? glyph.color : 'none'}
      stroke={glyph.color}
    >
      {geometry}
    </svg>
  )
}
